import { BARRIER_HEIGHT, CUBE_SIZE, FLOOR_Y, LASER_BOTTOM_Y } from "../constants";
import { GRAVITY, TERMINAL_VELOCITY } from "../config";
import { GameColor } from "../types";

const HALF_CUBE = CUBE_SIZE / 2;
const LANDING_TOLERANCE_PX = 2; // small overlap still counts as standing on a barrier

export interface PlayerBody {
    centerY: number;
    velocityY: number;
}

export interface BarrierBody {
    topY: number;
    color: GameColor;
    isPassedThrough: boolean;
}

export interface PlayerStep extends PlayerBody {
    isRidingBarrier: boolean;
}

export function isSolidFor(barrier: BarrierBody, playerColor: GameColor): boolean {
    return barrier.color !== playerColor && !barrier.isPassedThrough;
}

export function overlapsBarrier(playerCenterY: number, barrierTopY: number, topTolerancePx = 0): boolean {
    const cubeTopY = playerCenterY - HALF_CUBE;
    const cubeBottomY = playerCenterY + HALF_CUBE;
    return cubeBottomY >= barrierTopY - topTolerancePx && cubeTopY <= barrierTopY + BARRIER_HEIGHT;
}

export function isTouchingLaser(playerCenterY: number): boolean {
    return playerCenterY - HALF_CUBE <= LASER_BOTTOM_Y;
}

function restingCenterY(barrier: BarrierBody): number {
    return barrier.topY - HALF_CUBE;
}

// Screen y grows downwards, so the highest position is the smallest y
function highest(ys: number[]): number | null {
    return ys.length > 0 ? Math.min(...ys) : null;
}

/** Centre y of the cube if it is already sitting on (or pushed into) a barrier. */
function findRidingY(playerCenterY: number, solidBarriers: BarrierBody[]): number | null {
    const supporting = solidBarriers.filter((b) => overlapsBarrier(playerCenterY, b.topY, LANDING_TOLERANCE_PX));
    return highest(supporting.map(restingCenterY));
}

/** Centre y of the cube if it falls onto a barrier between `fromY` and `toY` this frame. */
function findLandingY(fromY: number, toY: number, solidBarriers: BarrierBody[]): number | null {
    const crossed = solidBarriers
        .map(restingCenterY)
        .filter((restY) => fromY <= restY + LANDING_TOLERANCE_PX && toY >= restY);
    return highest(crossed);
}

/**
 * Moves the player one frame. Barriers that don't match the player's colour are
 * solid: the cube rides them upwards. Otherwise it falls under gravity until it
 * lands on one or hits the floor.
 */
export function stepPlayer(
    player: PlayerBody,
    barriers: readonly BarrierBody[],
    playerColor: GameColor,
    dt: number,
): PlayerStep {
    const solidBarriers = barriers.filter((b) => isSolidFor(b, playerColor));

    const ridingY = findRidingY(player.centerY, solidBarriers);
    if (ridingY !== null) {
        return { centerY: ridingY, velocityY: 0, isRidingBarrier: true };
    }

    const velocityY = Math.min(player.velocityY + GRAVITY * dt, TERMINAL_VELOCITY);
    const fallenY = player.centerY + velocityY * dt;
    const landedY = findLandingY(player.centerY, fallenY, solidBarriers);
    const floorY = FLOOR_Y - HALF_CUBE;
    const centerY = Math.min(landedY ?? fallenY, floorY);
    const hasStopped = landedY !== null || centerY === floorY;

    return { centerY, velocityY: hasStopped ? 0 : velocityY, isRidingBarrier: false };
}
